import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

type AnimateCounterProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
  endValue?: number;
  startValue?: number;
  duration?: number;
  easing?: 'linear' | 'easeOut' | 'easeIn' | 'easeInOut';
  decimals?: number;
  prefix?: string;
  suffix?: string;
  trigger?: 'immediate' | 'visible';
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const easingFunctions = {
  linear: (t: number) => t,
  easeOut: (t: number) => 1 - (1 - t) * (1 - t),
  easeIn: (t: number) => t * t,
  easeInOut: (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
};

const AnimateCounter = (componentProps: AnimateCounterProps) => {
  let props = componentProps;

  const existingDataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, existingDataElementContext);

  const endValue = props.properties?.endValue ?? 0;
  const startValue = props.properties?.startValue ?? 0;
  const duration = props.properties?.duration ?? 800;
  const easing = (props.properties?.easing ?? 'easeOut') as keyof typeof easingFunctions;
  const decimals = props.properties?.decimals ?? 0;
  const prefix = props.properties?.prefix ?? '';
  const suffix = props.properties?.suffix ?? '';
  const trigger = props.properties?.trigger ?? 'immediate';

  const [displayValue, setDisplayValue] = useState(startValue);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();
  const currentValueRef = useRef(startValue);

  const animateCounter = useCallback((from: number, to: number) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const startTime = performance.now();
    const difference = to - from;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easedProgress = easingFunctions[easing](progress);
      const currentValue = from + (difference * easedProgress);
      
      setDisplayValue(currentValue);
      currentValueRef.current = currentValue;

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setHasAnimated(true);
        setDisplayValue(to);
        currentValueRef.current = to;
      }
    };

    animate();
  }, [duration, easing]);

  useEffect(() => {
    if (trigger !== 'visible' || !elementRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(elementRef.current);

    return () => observer.disconnect();
  }, [trigger, hasAnimated]);

  useEffect(() => {
    if (trigger === 'immediate' && endValue !== currentValueRef.current) {
      animateCounter(currentValueRef.current, endValue);
    }
  }, [endValue, trigger, animateCounter]);

  useEffect(() => {
    if (trigger === 'visible' && isVisible && !hasAnimated) {
      animateCounter(currentValueRef.current, endValue);
    }
  }, [isVisible, hasAnimated, endValue, trigger, animateCounter]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const formatValue = (value: number) => {
    const formattedNumber = decimals > 0 
      ? value.toFixed(decimals)
      : Math.round(value).toString();
    
    return `${prefix}${formattedNumber}${suffix}`;
  };

  const contextValue = React.useMemo(() => {
    return {
      ...existingDataElementContext,
      displayValue,
      formatValue,
      formattedValue: formatValue(displayValue),
      isAnimating: animationRef.current !== undefined,
      animateCounter: (to: number) => animateCounter(currentValueRef.current, to)
    };
  }, [existingDataElementContext, displayValue, formatValue, animateCounter]);

  return (
    <ModuleElementDiv ref={elementRef} className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>
        {componentProps.children}
      </DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default AnimateCounter;
